import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { modalRoutes } from 'constants/routes';
import useSettingsV2Gate from 'app/hooks/useSettingsV2Gate';
import { PageData, ProfileGroup } from 'types/Page';
import ProfileSelector from 'app/components/ProfileSelector';
import { PageThunk } from 'modules/page/pageActions';
import EditProfileGroupSheet from 'app/modules/settings/modules/profileGroups/components/EditProfileGroupSheet';
import PostsProfileSelectorDataList from './postsProfileSelector/PostsProfileSelectorDataList';
import PostsProfileSelectorFooter from './postsProfileSelector/PostsProfileSelectorFooter';
import usePostsSelectorFilter from './postsProfileSelector/hooks/usePostsSelectorFilter';
import { ProfileSelectorFilterOptionKey } from 'app/components/profileSelector/types/profileSelectorFilter';
import getProfileSelectorTitleByFilterOption from 'app/components/profileSelector/utils/getProfileSelectorTitleByFilterOption';
import { ChangePostsFilterHandler } from 'app/modules/posts/hooks/useFilterState';
import DemoContentToggle from 'app/modules/demoContent/components/DemoContentToggle';

type Props = {
  profiles: PageData[];
  profileGroups: ProfileGroup[];
  selectedProfilesIds: number[];
  selectedProfileGroupsIds: number[];
  onChange: ChangePostsFilterHandler;
};

const MIN_PROFILES_FOR_BULK_SELECTION = 2;

const PostsProfileSelector: FC<Props> = ({
  profiles,
  profileGroups,
  selectedProfilesIds,
  selectedProfileGroupsIds,
  onChange,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isSettingsV2Enabled } = useSettingsV2Gate();
  const [editGroupId, setEditGroupId] = useState<number | null>(null);

  const handleEditProfileGroup = (id: number) => {
    if (isSettingsV2Enabled) {
      setEditGroupId(id);
      return;
    }

    navigate({ hash: modalRoutes.MANAGE_PROFILE_GROUP, search: `?id=${id}` });
  };

  const handleEditProfileGroupClose = () => {
    setEditGroupId(null);
    dispatch(PageThunk.requestPages());
  };

  const {
    sidebarFilterOptions,
    searchQuery,
    selectedFilter,
    filteredOptions,
    selectFilter,
    setSearchQuery,
    initialOpen,
  } = usePostsSelectorFilter({
    profiles,
    profileGroups,
    selectedProfileGroupsIds,
    selectedProfilesIds,
  });

  const handleToggleAllSelection = (selectionOption: 'all' | 'none') => {
    if (selectionOption === 'none') {
      const profileIds = profiles.map((profile) => profile.id);

      if (profileIds.length) {
        onChange(
          {
            key: 'selectedProfiles',
            value: [profileIds[0]],
          },
          {
            key: 'selectedGroups',
            value: [],
          },
        );
        selectFilter(ProfileSelectorFilterOptionKey.All);
      }
    } else {
      if (getIsSelectedAll()) return;

      switch (selectedFilter) {
        case ProfileSelectorFilterOptionKey.All:
        case ProfileSelectorFilterOptionKey.Selected:
          selectProfileGroups();
          selectProfiles();
          selectFilter(ProfileSelectorFilterOptionKey.All);
          break;
        case ProfileSelectorFilterOptionKey.ProfileGroups:
          selectProfileGroups();
          break;
        case ProfileSelectorFilterOptionKey.Profiles:
        default:
          selectProfiles();
          break;
      }
    }
  };

  const selectProfileGroups = () => {
    const profileGroupsIds = (
      filteredOptions as (ProfileGroup & {
        isGroup?: boolean;
      })[]
    )
      .filter((option) => option.isGroup)
      .map((profileGroup) => profileGroup.id);

    onChange({
      key: 'selectedGroups',
      value: profileGroupsIds,
    });
  };

  const selectProfiles = () => {
    const profileIds = (
      filteredOptions as (PageData & {
        isGroup?: boolean;
      })[]
    )
      .filter((option) => !option.isGroup && !option.isDemo)
      .map((profile) => profile.id);

    onChange({
      key: 'selectedProfiles',
      value: profileIds,
    });
  };

  const getIsSelectedAll = () => {
    const nonDemoProfiles = profiles.filter((profile) => !profile.isDemo);

    switch (selectedFilter) {
      case ProfileSelectorFilterOptionKey.All:
        return (
          profileGroups.length === selectedProfileGroupsIds.length &&
          nonDemoProfiles.length === selectedProfilesIds.length
        );
      case ProfileSelectorFilterOptionKey.ProfileGroups:
        return profileGroups.length === selectedProfileGroupsIds.length;
      case ProfileSelectorFilterOptionKey.Selected:
        return true;
      default:
        return filteredOptions
          .filter((option) => !(option as PageData).isDemo)
          .map((profile) => profile.id)
          .every((profileId) => selectedProfilesIds.includes(profileId));
    }
  };

  const getShowBulkSelection = () => {
    const totalItems = profiles.length + profileGroups.length;
    const hasOnlyDemoPage =
      profiles.length === 1 &&
      profiles[0]?.isDemo &&
      profileGroups.length === 0;

    return totalItems >= MIN_PROFILES_FOR_BULK_SELECTION && !hasOnlyDemoPage;
  };

  const getPlaceholderData = () => {
    const pagesData = profiles.filter((page) =>
      selectedProfilesIds.includes(page.id),
    );
    const profileGroupsData = profileGroups.filter((profileGroup) =>
      selectedProfileGroupsIds.includes(profileGroup.id),
    );

    return [...profileGroupsData, ...pagesData].map((option) => ({
      ...option,
      logoSrc: option.logo?.src,
    }));
  };
  return (
    <>
      <ProfileSelector
        placeholderData={getPlaceholderData()}
        dataName={{ placeholder: 'posts-profile-select' }}
        title={getProfileSelectorTitleByFilterOption(selectedFilter)}
        isSelectedAll={getIsSelectedAll()}
        initialOpen={
          initialOpen ||
          profiles.length === 0 ||
          (profiles.length === 1 && profiles[0]?.isDemo)
        }
        filter={{
          searchQuery,
          onSearch: setSearchQuery,
          selectedFilterOption: selectedFilter,
          options: sidebarFilterOptions,
          onSelectFilterOption: selectFilter,
          footer: <DemoContentToggle />,
        }}
        optionsElement={
          <PostsProfileSelectorDataList
            data={filteredOptions}
            selectedProfileGroupsIds={selectedProfileGroupsIds}
            onSelectProfileGroup={(id, multiple) => {
              if (multiple) {
                onChange({ key: 'selectedGroups', value: id });
              } else {
                onChange(
                  {
                    key: 'selectedProfiles',
                    value: [],
                  },
                  {
                    key: 'selectedGroups',
                    value: [id],
                  },
                );
              }
            }}
            selectedProfilesIds={selectedProfilesIds}
            onSelectProfile={(id, multiple) => {
              if (multiple) {
                onChange({ key: 'selectedProfiles', value: id });
              } else {
                onChange(
                  {
                    key: 'selectedProfiles',
                    value: [id],
                  },
                  {
                    key: 'selectedGroups',
                    value: [],
                  },
                );
              }
            }}
            selectedFilter={selectedFilter}
            onEditProfileGroup={handleEditProfileGroup}
          />
        }
        onToggleAllSelection={
          getShowBulkSelection() ? handleToggleAllSelection : undefined
        }
        footer={<PostsProfileSelectorFooter />}
      />
      {editGroupId !== null && (
        <EditProfileGroupSheet
          groupId={editGroupId}
          isOpen
          onClose={handleEditProfileGroupClose}
        />
      )}
    </>
  );
};

export default PostsProfileSelector;
